import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

/**
 * TRP-4453: the chat carousel renders `OfferCardMapper` directly instead of `OfferCard`, so it
 * needs its own card shell.
 *
 * This is a COPY of `OfferCard`'s `Card`, not an extraction: the rules below are duplicated
 * from it and the original is untouched — `OfferCard.tsx` is not in this branch's diff, and it
 * still carries the `compact-hotel-card` / `size-small` blocks. An earlier version of this
 * comment said those blocks had been *moved* out of it; they had not (review #37389 [25/50]).
 *
 * So the two shells will drift. Resolving that means exporting the shell from `OfferCards/` and
 * importing it in both places — deliberately not done on this branch, because `OfferCard` is a
 * shared component well outside this migration's blast radius. Filed as a follow-up.
 */
const CarouselOfferCard = styled.div`
  display: grid;
  width: 80vw;
  height: 100%;
  flex-shrink: 0;

  // ChatHotelCarouselCard places a full-card click target at z-index 1 to open
  // the offer drawer. Keep controls rendered by the shared offer card above it
  // so they receive their own click before the card-level target. NestableClickable's
  // invisible button must stay absolute or it takes space before the offer image.
  button:not([data-nestable-clickable-overlay]) {
    position: relative;
    z-index: 2;
  }

  // Sold out villas return null so need to be hidden
  &:empty {
    display: none;
  }

  > * {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }

  ${mediaQueryUp.desktop} {
    &.size-medium {
      width: ${rem(512)};
    }
  }

  &.compact-hotel-card,
  &.chat-cruise-carousel-card {
    width: 100%;
    min-width: 0;
    min-height: ${rem(346)};
    gap: ${rem(8)};

    > * {
      width: 100%;
      min-width: 0;
    }

    > * > :first-child {
      height: ${rem(158)};
      aspect-ratio: auto;
    }

    > * > :last-child {
      gap: ${rem(8)};
    }

    ${mediaQueryUp.desktop} {
      width: ${rem(320)};
      min-height: ${rem(300)};
      gap: ${rem(16)};

      > * > :first-child {
        height: auto;
        aspect-ratio: 16 / 9;
      }

      > * > :last-child {
        gap: ${rem(16)};
      }
    }
  }

  &.chat-experience-carousel-card {
    width: 100%;
    min-width: 0;
    min-height: ${rem(346)};
    gap: ${rem(8)};

    // ExperienceOfferCard wraps the card in an extra element that LEOfferCard omits,
    // so these styles must reach one level deeper than .compact-hotel-card.
    > *,
    > * > * {
      width: 100%;
      min-width: 0;
    }

    > * > * > :first-child {
      height: ${rem(158)};
      aspect-ratio: auto;
    }

    > * > * > :last-child {
      gap: ${rem(8)};
    }

    ${mediaQueryUp.desktop} {
      width: ${rem(320)};
      min-height: ${rem(300)};
      gap: ${rem(16)};

      > * > * > :first-child {
        height: auto;
        aspect-ratio: 16 / 9;
      }

      > * > * > :last-child {
        gap: ${rem(16)};
      }
    }
  }

  /* Fix width for hidden offer cards to prevent overlap on tablet and larger screens */
  &:has(.lux-plus-hidden-offer-card) {
    ${mediaQueryUp.tablet} {
      width: ${rem(512)};

      &.size-small {
        width: ${rem(320)};
      }
    }

    &.compact-hotel-card {
      width: 100%;

      ${mediaQueryUp.desktop} {
        width: ${rem(320)};
      }
    }
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

export default CarouselOfferCard
